import { useEffect, useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, ExternalLink, AlertCircle, CheckCircle2, Loader2, Clock, History } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intentColors = {
  "Outage / Service Down": "bg-[#FF2A2A]/10 text-[#FF2A2A] border-[#FF2A2A]/20",
  "Hardware Failure": "bg-[#FFB800]/10 text-[#FFB800] border-[#FFB800]/20",
  "Configuration / Setup": "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  "Billing / Account": "bg-[#0A0A0A]/10 text-[#0A0A0A] border-[#0A0A0A]/20",
  "Feature Request": "bg-[#00C853]/10 text-[#00C853] border-[#00C853]/20",
  "General Inquiry": "bg-gray-100 text-gray-600 border-gray-200",
  "Security Incident / Breach Alert": "bg-[#FF2A2A]/20 text-[#FF2A2A] border-[#FF2A2A]/40",
  "Onboarding / System Commissioning": "bg-[#FFB800]/20 text-[#FFB800] border-[#FFB800]/40",
};

const priorityRibbon = {
  Low: "border-l-[#9CA3AF]",
  Medium: "border-l-[#002FA7]",
  High: "border-l-[#FFB800]",
  Urgent: "border-l-[#FF2A2A]",
};

function CrmStatusBadge({ status, error }) {
  if (status === "synced") {
    return (
      <span className="flex items-center gap-1 text-[10px] font-bold text-[#00C853]">
        <CheckCircle2 size={11} /> CRM SYNCED
      </span>
    );
  }
  if (status === "pushing") {
    return (
      <span className="flex items-center gap-1 text-[10px] font-bold text-[#002FA7]">
        <Loader2 size={11} className="animate-spin" /> PUSHING
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="flex items-center gap-1 text-[10px] font-bold text-[#FF2A2A]" title={error || ""}>
        <AlertCircle size={11} /> FAILED
      </span>
    );
  }
  if (status === "skipped") {
    return <span className="text-[10px] font-bold text-[#9CA3AF]">SKIPPED</span>;
  }
  return <span className="text-[10px] text-[#9CA3AF]">—</span>;
}

export default function RecentCallsPanel({ t }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await ax.get(`${API}/calls/recent`);
      setCalls(res.data || []);
    } catch {
      // silent — best-effort
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const retryPush = async (callSid) => {
    setRetrying((s) => ({ ...s, [callSid]: true }));
    try {
      await ax.post(`${API}/calls/${callSid}/crm-push`);
      toast.success("CRM sync queued");
      setTimeout(load, 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying((s) => ({ ...s, [callSid]: false }));
    }
  };

  return (
    <div className="border border-[#E5E7EB] bg-white p-6" data-testid="recent-calls-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#0A0A0A]">
            <History size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">{t("operator.recent_calls") || "Recent Calls"}</h3>
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#9CA3AF] font-bold">
              {calls.length} captured · CRM sync status
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none h-7 text-[10px] font-bold tracking-[0.1em] uppercase"
          onClick={load}
          disabled={loading}
          data-testid="refresh-recent-calls"
        >
          <RefreshCw size={11} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {calls.length === 0 && (
        <p className="text-sm text-[#9CA3AF] text-center py-6" data-testid="recent-calls-empty">
          No calls yet. They will appear here after the first call completes.
        </p>
      )}

      <div className="space-y-2 max-h-[440px] overflow-y-auto thin-scrollbar">
        {calls.map((c) => {
          const intent = c.intent || "—";
          const intentCls = intentColors[intent] || "bg-gray-100 text-gray-600 border-gray-200";
          const ribbon = priorityRibbon[c.priority] || "border-l-[#E5E7EB]";
          const duration = c.duration_seconds || 0;
          const minutes = Math.floor(duration / 60);
          const seconds = duration % 60;
          return (
            <div
              key={c.call_sid}
              className={`p-3 bg-[#F8F9FA] border border-[#E5E7EB] border-l-4 ${ribbon}`}
              data-testid={`recent-call-${c.call_sid}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="rounded-none text-[9px] font-bold uppercase">
                      {(c.direction || "—").toUpperCase()}
                    </Badge>
                    <span className="text-xs font-bold text-[#0A0A0A] truncate">
                      {c.direction === "inbound" ? c.from : c.to}
                    </span>
                  </div>
                  <p className="text-[10px] text-[#9CA3AF] font-mono mt-0.5 truncate">
                    {c.call_sid}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-[10px] text-[#4B5563]">
                  <Clock size={10} />
                  {minutes}:{String(seconds).padStart(2, "0")}
                </div>
              </div>

              {intent !== "—" && (
                <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                  <Badge variant="outline" className={`rounded-none text-[9px] font-bold ${intentCls}`}>
                    {intent}
                  </Badge>
                  {c.priority && (
                    <Badge variant="outline" className="rounded-none text-[9px] font-bold uppercase">
                      {c.priority}
                    </Badge>
                  )}
                </div>
              )}

              {c.intent_summary && (
                <p className="text-[10px] text-[#4B5563] leading-relaxed line-clamp-2 mb-1.5">
                  {c.intent_summary}
                </p>
              )}

              <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-[#E5E7EB]">
                <CrmStatusBadge status={c.crm_status} error={c.crm_error} />
                <div className="flex gap-1">
                  {c.crm_ticket_url && (
                    <a
                      href={c.crm_ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[10px] font-bold text-[#002FA7] hover:underline"
                      data-testid={`crm-ticket-link-${c.call_sid}`}
                    >
                      <ExternalLink size={10} /> Ticket {c.crm_ticket_id}
                    </a>
                  )}
                  {(c.crm_status === "failed" || c.crm_status === "skipped" || !c.crm_status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-none h-6 text-[9px] font-bold tracking-[0.1em] uppercase px-2"
                      onClick={() => retryPush(c.call_sid)}
                      disabled={!!retrying[c.call_sid]}
                      data-testid={`retry-crm-${c.call_sid}`}
                    >
                      <RefreshCw size={10} className={`mr-1 ${retrying[c.call_sid] ? "animate-spin" : ""}`} />
                      Retry CRM Sync
                    </Button>
                  )}
                </div>
              </div>

              {c.crm_status === "failed" && c.crm_error && (
                <p className="text-[9px] text-[#FF2A2A] mt-1 font-mono line-clamp-2" data-testid={`crm-error-${c.call_sid}`}>
                  {c.crm_error}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
